import { useState, useEffect } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { Save, X, AlertCircle } from 'lucide-react';
import PageHeader from '../../../components/PageHeader';
import StickyActions from '../../../components/StickyActions';
import { useCuenta, useCuentaMutations } from '../../../graphql/tesoreria';

export default function EditarCuenta() {
  const navigate = useNavigate();
  const { id } = useParams();
  const location = useLocation();
  const isEditing = !!id;

  const [formData, setFormData] = useState({
    banco: '',
    descripcion: '',
    moneda: 'ARS',
    tipo: 'CC',
    cbu: '',
    alias: '',
    numeroCuenta: '',
    titular: '',
    estado: 'ACTIVO',
    observaciones: '',
  });

  const [errors, setErrors] = useState({});
  const [saving, setSaving] = useState(false);

  // TODO: Usar el hook real cuando esté conectado el backend
  const [result] = useCuenta(id);
  const { createCuenta, updateCuenta } = useCuentaMutations();

  const cuenta = result.data?.cuentaBancaria;

  // Cargar datos si es edición o duplicado
  useEffect(() => {
    if (location.state?.duplicateFrom) {
      setFormData(location.state.duplicateFrom);
    } else if (cuenta && isEditing) {
      setFormData({
        banco: cuenta.banco || '',
        descripcion: cuenta.descripcion || '',
        moneda: cuenta.moneda || 'ARS',
        tipo: cuenta.tipo || 'CC',
        cbu: cuenta.cbu || '',
        alias: cuenta.alias || '',
        numeroCuenta: cuenta.numeroCuenta || '',
        titular: cuenta.titular || '',
        estado: cuenta.estado || 'ACTIVO',
        observaciones: cuenta.observaciones || '',
      });
    }
  }, [cuenta, isEditing, location.state]);

  // Atajo de teclado Ctrl/Cmd+S para guardar
  useEffect(() => {
    const handleKeyDown = (e) => {
      if ((e.ctrlKey || e.metaKey) && e.key === 's') {
        e.preventDefault();
        handleSubmit(e);
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [formData]);

  const handleChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    // Limpiar error del campo al modificarlo
    if (errors[field]) {
      setErrors((prev) => {
        const newErrors = { ...prev };
        delete newErrors[field];
        return newErrors;
      });
    }
  };

  const validate = () => {
    const newErrors = {};

    if (!formData.banco.trim()) {
      newErrors.banco = 'El banco es obligatorio';
    }

    if (!formData.moneda) {
      newErrors.moneda = 'La moneda es obligatoria';
    }

    if (!formData.titular.trim()) {
      newErrors.titular = 'El titular es obligatorio';
    }

    return newErrors;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const validationErrors = validate();
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      return;
    }

    setSaving(true);

    try {
      if (isEditing) {
        await updateCuenta({ id, input: formData });
        // TODO: Mostrar toast de éxito
        alert('Cuenta actualizada correctamente');
      } else {
        await createCuenta({ input: formData });
        // TODO: Mostrar toast de éxito
        alert('Cuenta creada correctamente');
      }
      navigate('/Tesoreria/Cuentas');
    } catch (error) {
      // TODO: Mostrar toast de error
      alert(`Error al ${isEditing ? 'actualizar' : 'crear'} la cuenta`);
      console.error(error);
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = () => {
    if (
      Object.values(formData).some((value) => value !== '') &&
      !window.confirm('¿Desea descartar los cambios?')
    ) {
      return;
    }
    navigate('/Tesoreria/Cuentas');
  };

  return (
    <div className="min-h-screen bg-[rgba(var(--bg))] pb-24">
      <PageHeader
        title={isEditing ? 'Editar Cuenta Bancaria' : 'Nueva Cuenta Bancaria'}
        subtitle={
          isEditing
            ? `Modificar información de la cuenta`
            : 'Registrar una nueva cuenta bancaria'
        }
      />

      <section className="max-w-[1400px] mx-auto px-4 py-6 grid grid-cols-12 gap-4">
        <div className="col-span-12 rounded-2xl border border-[rgb(var(--border))] bg-[rgba(var(--panel))] shadow-[var(--shadow)] p-6">
          <form onSubmit={handleSubmit}>
            <div className="grid grid-cols-12 gap-4">
              {/* Banco */}
              <div className="col-span-12 md:col-span-6">
                <label className="block text-sm font-medium text-[rgb(var(--fg))] mb-1.5">
                  Banco <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={formData.banco}
                  onChange={(e) => handleChange('banco', e.target.value)}
                  className={`w-full px-4 py-2.5 rounded-xl border bg-[rgba(var(--bg))] text-[rgb(var(--fg))] placeholder:text-[rgb(var(--muted))] transition-colors focus:outline-none focus:ring-2 focus:ring-[rgb(var(--ring))] ${
                    errors.banco
                      ? 'border-red-500 focus:border-red-500'
                      : 'border-[rgb(var(--border))] focus:border-[rgb(var(--brand))]'
                  }`}
                  placeholder="Ej: Banco Galicia"
                  aria-invalid={!!errors.banco}
                  aria-describedby={errors.banco ? 'banco-error' : undefined}
                />
                {errors.banco && (
                  <p
                    id="banco-error"
                    className="mt-1 text-sm text-red-500 flex items-center gap-1"
                    role="alert"
                  >
                    <AlertCircle className="w-3.5 h-3.5" aria-hidden="true" />
                    {errors.banco}
                  </p>
                )}
              </div>

              {/* Descripción */}
              <div className="col-span-12 md:col-span-6">
                <label className="block text-sm font-medium text-[rgb(var(--fg))] mb-1.5">
                  Descripción
                </label>
                <input
                  type="text"
                  value={formData.descripcion}
                  onChange={(e) => handleChange('descripcion', e.target.value)}
                  className="w-full px-4 py-2.5 rounded-xl border border-[rgb(var(--border))] bg-[rgba(var(--bg))] text-[rgb(var(--fg))] placeholder:text-[rgb(var(--muted))] focus:outline-none focus:ring-2 focus:ring-[rgb(var(--ring))] focus:border-[rgb(var(--brand))]"
                  placeholder="Ej: Cuenta Operativa Principal"
                />
              </div>

              {/* Moneda */}
              <div className="col-span-12 md:col-span-4">
                <label className="block text-sm font-medium text-[rgb(var(--fg))] mb-1.5">
                  Moneda <span className="text-red-500">*</span>
                </label>
                <select
                  value={formData.moneda}
                  onChange={(e) => handleChange('moneda', e.target.value)}
                  className={`w-full px-4 py-2.5 rounded-xl border bg-[rgba(var(--bg))] text-[rgb(var(--fg))] focus:outline-none focus:ring-2 focus:ring-[rgb(var(--ring))] ${
                    errors.moneda
                      ? 'border-red-500 focus:border-red-500'
                      : 'border-[rgb(var(--border))] focus:border-[rgb(var(--brand))]'
                  }`}
                  aria-invalid={!!errors.moneda}
                >
                  <option value="ARS">ARS - Peso Argentino</option>
                  <option value="USD">USD - Dólar</option>
                  <option value="EUR">EUR - Euro</option>
                </select>
                {errors.moneda && (
                  <p className="mt-1 text-sm text-red-500" role="alert">
                    {errors.moneda}
                  </p>
                )}
              </div>

              {/* Tipo */}
              <div className="col-span-12 md:col-span-4">
                <label className="block text-sm font-medium text-[rgb(var(--fg))] mb-1.5">
                  Tipo de Cuenta
                </label>
                <select
                  value={formData.tipo}
                  onChange={(e) => handleChange('tipo', e.target.value)}
                  className="w-full px-4 py-2.5 rounded-xl border border-[rgb(var(--border))] bg-[rgba(var(--bg))] text-[rgb(var(--fg))] focus:outline-none focus:ring-2 focus:ring-[rgb(var(--ring))] focus:border-[rgb(var(--brand))]"
                >
                  <option value="CA">Caja de Ahorro</option>
                  <option value="CC">Cuenta Corriente</option>
                </select>
              </div>

              {/* Estado */}
              <div className="col-span-12 md:col-span-4">
                <label className="block text-sm font-medium text-[rgb(var(--fg))] mb-1.5">
                  Estado
                </label>
                <select
                  value={formData.estado}
                  onChange={(e) => handleChange('estado', e.target.value)}
                  className="w-full px-4 py-2.5 rounded-xl border border-[rgb(var(--border))] bg-[rgba(var(--bg))] text-[rgb(var(--fg))] focus:outline-none focus:ring-2 focus:ring-[rgb(var(--ring))] focus:border-[rgb(var(--brand))]"
                >
                  <option value="ACTIVO">Activo</option>
                  <option value="INACTIVO">Inactivo</option>
                </select>
              </div>

              {/* CBU */}
              <div className="col-span-12 md:col-span-6">
                <label className="block text-sm font-medium text-[rgb(var(--fg))] mb-1.5">
                  CBU
                </label>
                <input
                  type="text"
                  value={formData.cbu}
                  onChange={(e) => handleChange('cbu', e.target.value)}
                  className="w-full px-4 py-2.5 rounded-xl border border-[rgb(var(--border))] bg-[rgba(var(--bg))] text-[rgb(var(--fg))] placeholder:text-[rgb(var(--muted))] focus:outline-none focus:ring-2 focus:ring-[rgb(var(--ring))] focus:border-[rgb(var(--brand))]"
                  placeholder="0000000000000000000000"
                  maxLength={22}
                />
              </div>

              {/* Alias */}
              <div className="col-span-12 md:col-span-6">
                <label className="block text-sm font-medium text-[rgb(var(--fg))] mb-1.5">
                  Alias
                </label>
                <input
                  type="text"
                  value={formData.alias}
                  onChange={(e) => handleChange('alias', e.target.value)}
                  className="w-full px-4 py-2.5 rounded-xl border border-[rgb(var(--border))] bg-[rgba(var(--bg))] text-[rgb(var(--fg))] placeholder:text-[rgb(var(--muted))] focus:outline-none focus:ring-2 focus:ring-[rgb(var(--ring))] focus:border-[rgb(var(--brand))]"
                  placeholder="ALIAS.BANCO.CUENTA"
                />
              </div>

              {/* Número de Cuenta */}
              <div className="col-span-12 md:col-span-6">
                <label className="block text-sm font-medium text-[rgb(var(--fg))] mb-1.5">
                  Número de Cuenta
                </label>
                <input
                  type="text"
                  value={formData.numeroCuenta}
                  onChange={(e) => handleChange('numeroCuenta', e.target.value)}
                  className="w-full px-4 py-2.5 rounded-xl border border-[rgb(var(--border))] bg-[rgba(var(--bg))] text-[rgb(var(--fg))] placeholder:text-[rgb(var(--muted))] focus:outline-none focus:ring-2 focus:ring-[rgb(var(--ring))] focus:border-[rgb(var(--brand))]"
                  placeholder="12345678"
                />
              </div>

              {/* Titular */}
              <div className="col-span-12 md:col-span-6">
                <label className="block text-sm font-medium text-[rgb(var(--fg))] mb-1.5">
                  Titular <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={formData.titular}
                  onChange={(e) => handleChange('titular', e.target.value)}
                  className={`w-full px-4 py-2.5 rounded-xl border bg-[rgba(var(--bg))] text-[rgb(var(--fg))] placeholder:text-[rgb(var(--muted))] focus:outline-none focus:ring-2 focus:ring-[rgb(var(--ring))] ${
                    errors.titular
                      ? 'border-red-500 focus:border-red-500'
                      : 'border-[rgb(var(--border))] focus:border-[rgb(var(--brand))]'
                  }`}
                  placeholder="Ej: Empresa SRL"
                  aria-invalid={!!errors.titular}
                />
                {errors.titular && (
                  <p className="mt-1 text-sm text-red-500 flex items-center gap-1" role="alert">
                    <AlertCircle className="w-3.5 h-3.5" aria-hidden="true" />
                    {errors.titular}
                  </p>
                )}
              </div>

              {/* Observaciones */}
              <div className="col-span-12">
                <label className="block text-sm font-medium text-[rgb(var(--fg))] mb-1.5">
                  Observaciones
                </label>
                <textarea
                  value={formData.observaciones}
                  onChange={(e) => handleChange('observaciones', e.target.value)}
                  rows={3}
                  className="w-full px-4 py-2.5 rounded-xl border border-[rgb(var(--border))] bg-[rgba(var(--bg))] text-[rgb(var(--fg))] placeholder:text-[rgb(var(--muted))] focus:outline-none focus:ring-2 focus:ring-[rgb(var(--ring))] focus:border-[rgb(var(--brand))] resize-none"
                  placeholder="Notas adicionales sobre la cuenta..."
                />
              </div>
            </div>
          </form>
        </div>
      </section>

      {/* Sticky Actions */}
      <StickyActions
        actions={[
          <button
            key="guardar"
            onClick={handleSubmit}
            disabled={saving}
            className="flex items-center gap-2 px-6 py-2.5 rounded-xl bg-[rgb(var(--brand))] text-white hover:bg-[rgb(var(--brand-700))] disabled:opacity-50 disabled:cursor-not-allowed transition-colors focus:outline-none focus:ring-2 focus:ring-[rgb(var(--ring))]"
          >
            <Save className="w-4 h-4" aria-hidden="true" />
            {saving ? 'Guardando...' : 'Guardar'}
          </button>,
          <button
            key="cancelar"
            onClick={handleCancel}
            disabled={saving}
            className="flex items-center gap-2 px-6 py-2.5 rounded-xl border border-[rgb(var(--border))] text-[rgb(var(--fg))] hover:bg-[rgba(var(--muted),0.3)] disabled:opacity-50 disabled:cursor-not-allowed transition-colors focus:outline-none focus:ring-2 focus:ring-[rgb(var(--ring))]"
          >
            <X className="w-4 h-4" aria-hidden="true" />
            Cancelar
          </button>,
        ]}
      />
    </div>
  );
}
